// GENERAL
import React from "react";
import { HashRouter, Redirect, Route, Switch } from "react-router-dom";
import GAListener from "./components/GAListener";
import { EmptyLayout, LayoutRoute, MainLayout } from "./components/Layout";
import componentQueries from "react-component-queries";
import PageSpinner from "./components/PageSpinner";
import {
  can,
  getStoredUser,
  hasModule,
  isGestorProyectos,
  isPlatformAdmin,
} from "./utils/permissions";
import "./styles/reduction.scss";
// FIN GENERAL

// CONTEX
import AuthState from "./context/autenticacion/authState";
import tokenAuth from "./config/token";
import AlertaState from "./context/alertas/alertaState";
import GeneralState from "./context/general/generalState";
import UsuarioState from "./context/usuarios/usuarioState";
import ColegioState from "./context/colegios/colegioState";
import CodigoState from "./context/codigos/codigoState";
import ArticuloState from "./context/articulos/articuloState";
import PrestamoState from "./context/prestamos/prestamoState";
import ProyectoState from "./context/proyectos/proyectoState";
import ReporteState from "./context/reportes/reporteState";
// FIN CONTEX

// COMPONENTES
import Login from "./components/auth/Login";

const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const Usuarios = React.lazy(() => import("./components/usuarios"));
const Establecimientos = React.lazy(() => import("./components/colegios"));
const Codigos = React.lazy(() => import("./components/codigos"));
const Articulos = React.lazy(() => import("./components/articulos"));
const Prestamos = React.lazy(() => import("./components/prestamos"));
const Perfil = React.lazy(() => import("./components/cuenta"));
const Eventos = React.lazy(() => import("./components/eventos"));
const Contabilidad = React.lazy(() => import("./components/contabilidad"));
const ContabilidadV2 = React.lazy(() => import("./components/contabilidadV2"));
const Certificados = React.lazy(() => import("./components/certificados"));
const FlujoCaja = React.lazy(() => import("./components/contabilidad/FlujoCaja"));
const EmpresasSaas = React.lazy(() => import("./components/platform/EmpresasSaas"));
const ClientesProveedores = React.lazy(() =>
  import("./components/clientesProveedores")
);
const ValidacionCertificadoPublica = React.lazy(() =>
  import("./components/certificados/ValidacionCertificadoPublica")
);
const FirmaProfesorPublica = React.lazy(() =>
  import("./components/certificados/FirmaProfesorPublica")
);
const RecepcionConformePublica = React.lazy(() =>
  import("./components/recepcion/RecepcionConformePublica")
);
// FIN COMPONENTES

// REVISAR TOKEN
const token = localStorage.getItem("token");
if (token) {
  tokenAuth(token);
}
// FIN REVISAR TOKEN

const redirectPrivado = (usuario) =>
  isGestorProyectos(usuario) ? "/eventos" : "/dashboard";

const ModuleRoute = ({
  component: Component,
  moduleKey,
  permission,
  platformOnly = false,
  allowedForGestorProyectos = false,
  gestorPuedeUsarSinModulo = false,
  ...rest
}) => (
  <Route
    {...rest}
    render={(props) => {
      const usuario = getStoredUser();
      if (!usuario) return <Redirect to="/" />;

      const gestor = isGestorProyectos(usuario);
      if (gestor && !allowedForGestorProyectos) {
        return <Redirect to="/eventos" />;
      }

      const redirectTo = redirectPrivado(usuario);
      if (platformOnly && !isPlatformAdmin(usuario)) {
        return <Redirect to={redirectTo} />;
      }
      if (permission && !can(permission, usuario)) {
        return <Redirect to={redirectTo} />;
      }
      if (
        moduleKey &&
        !hasModule(moduleKey, usuario) &&
        !(gestor && gestorPuedeUsarSinModulo)
      ) {
        return <Redirect to={redirectTo} />;
      }
      return <Component {...props} />;
    }}
  />
);

const App = (props) => {
  return (
    <AuthState>
      <AlertaState>
        <UsuarioState>
          <ColegioState>
            <ArticuloState>
              <PrestamoState>
                <CodigoState>
                  <GeneralState>
                    <ProyectoState>
                      <ReporteState>
                        <HashRouter>
                          <GAListener>
                            <Switch>
                              <LayoutRoute
                                exact
                                path="/"
                                layout={EmptyLayout}
                                component={(props) => <Login {...props} />}
                              />
                              <LayoutRoute
                                exact
                                path="/validar-certificado/:codigo"
                                layout={EmptyLayout}
                                component={(props) => (
                                  <React.Suspense fallback={<PageSpinner />}>
                                    <ValidacionCertificadoPublica {...props} />
                                  </React.Suspense>
                                )}
                              />
                              <LayoutRoute
                                exact
                                path="/firma-profesor/:token"
                                layout={EmptyLayout}
                                component={(props) => (
                                  <React.Suspense fallback={<PageSpinner />}>
                                    <FirmaProfesorPublica {...props} />
                                  </React.Suspense>
                                )}
                              />
                              <LayoutRoute
                                exact
                                path="/recepcion/:token"
                                layout={EmptyLayout}
                                component={(props) => (
                                  <React.Suspense fallback={<PageSpinner />}>
                                    <RecepcionConformePublica {...props} />
                                  </React.Suspense>
                                )}
                              />
                              <MainLayout breakpoint={props.breakpoint}>
                                <React.Suspense fallback={<PageSpinner />}>
                                  <ModuleRoute exact path="/dashboard" component={Dashboard} />
                                  <ModuleRoute exact path="/usuarios" permission="users.manage" component={Usuarios} />
                                  <ModuleRoute exact path="/platform/empresas" platformOnly component={EmpresasSaas} />
                                  <ModuleRoute exact path="/sucursales" moduleKey="sucursales" component={Establecimientos} />
                                  <ModuleRoute exact path="/eventos" moduleKey="proyectos" allowedForGestorProyectos component={Eventos} />
                                  <ModuleRoute exact path="/certificados" moduleKey="certificados" component={Certificados} />
                                  <ModuleRoute
                                    exact
                                    path="/clientesProveedores"
                                    moduleKey="clientes_proveedores"
                                    allowedForGestorProyectos
                                    gestorPuedeUsarSinModulo
                                    component={ClientesProveedores}
                                  />
                                  <ModuleRoute exact path="/contabilidad" moduleKey="contabilidad" component={Contabilidad} />
                                  <ModuleRoute
                                    exact
                                    path="/contabilidad-v2"
                                    moduleKey="contabilidad"
                                    permission="accounting.read"
                                    component={ContabilidadV2}
                                  />
                                  <ModuleRoute exact path="/flujo-caja" moduleKey="flujo_caja" component={FlujoCaja} />
                                  <ModuleRoute exact path="/inventario" moduleKey="inventario" component={Articulos} />
                                  <ModuleRoute exact path="/prestamos" moduleKey="prestamos" component={Prestamos} />
                                  <ModuleRoute exact path="/codigos" moduleKey="codigos" component={Codigos} />
                                  <ModuleRoute exact path="/perfil" allowedForGestorProyectos component={Perfil} />
                                </React.Suspense>
                              </MainLayout>
                              <Redirect to="/" />
                            </Switch>
                          </GAListener>
                        </HashRouter>
                      </ReporteState>
                    </ProyectoState>
                  </GeneralState>
                </CodigoState>
              </PrestamoState>
            </ArticuloState>
          </ColegioState>
        </UsuarioState>
      </AlertaState>
    </AuthState>
  );
};

const query = ({ width }) => {
  if (width < 575) {
    return { breakpoint: "xs" };
  }

  if (576 < width && width < 767) {
    return { breakpoint: "sm" };
  }

  if (768 < width && width < 991) {
    return { breakpoint: "md" };
  }

  if (992 < width && width < 1199) {
    return { breakpoint: "lg" };
  }

  if (width > 1200) {
    return { breakpoint: "xl" };
  }

  return { breakpoint: "xs" };
};

export default componentQueries(query)(App);